import LFCSelectFormCompany from '_components-with-data/inputs/LFCSelectFormCompany';
import LFCDialogTitle from '_components/feedback/LFCDialogTitle';
import LFCButton from '_components/inputs/LFCButton';
import LFCDatePicker from '_components/inputs/LFCDatePicker';
import LFCSelectForm from '_components/inputs/LFCSelectForm';
import LFCTextField from '_components/inputs/LFCTextField';
import ProgressBar from '_components/ProgressBar';
import {adminAPIAccess, handleInputChange, handleInputChange2} from '_logics/LFCUtil';
import awsConfiguration from 'awsConfiguration';
import {useSnackbar} from 'notistack';
import {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {Dialog, DialogActions, DialogContent, Grid, Typography} from '@mui/material';

export type editMode = 'New' | 'Mod';
export type rowData = {
  mode: editMode;
  use_type: string;
  company_id: string;
  email: string;
  user_id: string;
  user_name: string;
  locale: string;
  effective_date: string;
};
export const emptyRowData: rowData = {
  mode: 'New',
  use_type: '10',
  company_id: '1',
  email: '',
  user_id: '',
  user_name: '',
  locale: 'ja',
  effective_date: '9999-12-31'
};

interface Props {
  open: boolean;
  onClose: (isOk: boolean) => void;
  row: rowData;
}

const UserCreateDialog = (props: Props) => {
  const {t} = useTranslation();
  const {enqueueSnackbar} = useSnackbar();
  const [startProcess, setStartProcess] = useState(false);
  const formRef = useRef<HTMLFormElement>(null!);
  const [values, setValues] = useState(emptyRowData);

  const useTypeItems: {name: string; value: string}[] = [
    {name: 'エンドユーザ [本番]', value: '10'},
    {name: 'LWメンバー(開発以外) [検証,本番]', value: '20'},
    {name: 'LWメンバー(開発) [全環境]', value: '30'}
  ];
  const localeTypes: {name: string; value: string}[] = [
    {name: '日本語', value: 'ja'},
    {name: 'English', value: 'en'}
  ];

  /**
   * ユーザを作成する。
   * 終了後は、招待メールが飛ぶ。
   */
  const onCreateUser = () => {
    // input check
    if (!formRef.current?.reportValidity()) {
      return;
    }

    // Cognitoユーザ作成時、同時にm_userにレコード作成する環境を決定する
    type env_name = 'develop' | 'staging' | 'production';
    let user_type = '00';
    const target_env: env_name[] = [];
    switch (values.use_type) {
      case '10': // エンドユーザ用(本番)
        target_env.push('production');
        break;
      case '20': // 社内メンバー用
        user_type = '99';
        target_env.push('staging', 'production');
        break;
      case '30': // 開発メンバー用
        user_type = '99';
        target_env.push('develop', 'staging', 'production');
        break;
    }

    setStartProcess(true);

    adminAPIAccess({
      apipath: 'CreateUser',
      parameters: {
        user_pool_id: awsConfiguration.UserPoolId,
        company_id: values.company_id,
        user_id: values.user_id,
        user_name: values.user_name,
        email: values.email,
        is_dark: false,
        locale: values.locale,
        user_type: user_type,
        effective_date: values.effective_date,
        target_env: target_env
      }
    })
      .then(response => {
        switch (response.data.result_code) {
          case 0:
            enqueueSnackbar(
              t('message.登録されているアドレスにメールを送りました。ご確認下さい。'),
              {variant: 'success'}
            );
            props.onClose(true);
            break;
          case -1:
            console.log(
              `result_code:${response.data.result_code} / result_ex:${response.data.result_ex}`
            );
            switch (response.data.result_ex[0]) {
              case 'UsernameExistsException':
                enqueueSnackbar(
                  t('message.そのユーザーIDはすでに利用中です。他のIDで登録してください。'),
                  {
                    variant: 'error'
                  }
                );
                break;
              default:
                enqueueSnackbar(
                  `${t('message.エラーが発生しました。')} ${response.data.result_ex}`,
                  {
                    variant: 'error'
                  }
                );
                break;
            }
            break;
          case -2:
            //table登録エラー
            enqueueSnackbar(`${t('message.エラーが発生しました。')} ${response.data.result_ex}`, {
              variant: 'error'
            });
            break;
          case -3:
            enqueueSnackbar(
              `${t('message.作成可能なユーザ数の上限に達しているため、登録できません。上限=')}${
                response.data.result_ex[1]
              }`,
              {
                variant: 'error'
              }
            );
            break;
          default:
            throw new Error(
              `result_code:${response.data.result_code} / result_ex:${response.data.result_ex}`
            );
        }
      })
      .catch(error => {
        console.log(error);
        if (error.response.status === 401) {
          // 認証エラー
          enqueueSnackbar(
            t('message.セッションの有効期限が切れました。再度ログインしてください。'),
            {variant: 'error'}
          );
        } else {
          enqueueSnackbar(t(`aws.cognito.${error.message}`), {variant: 'error'});
        }
      })
      .finally(() => {
        setStartProcess(false);
      });
  };

  useEffect(() => {
    props.open && setValues(props.row);
  }, [props.open]);

  return (
    <Dialog
      open={props.open}
      onClose={(event: React.MouseEvent, reason: string) => {
        if (reason === 'backdropClick') {
          // 背景クリックでは閉じさせない
          return;
        }
        props.onClose(false);
      }}
      disableEscapeKeyDown
      maxWidth={'sm'}
    >
      <ProgressBar startProcess={startProcess} screenLock />
      <LFCDialogTitle onClose={() => props.onClose(false)}>
        {values.mode === 'New' ? t('新規') : t('変更')}
      </LFCDialogTitle>
      <DialogContent>
        <Grid container spacing={5} justifyContent={'center'}>
          <Grid item xs={12}>
            <Typography variant="subtitle1">{t('message.登録内容を入力してください')}</Typography>
          </Grid>
          <Grid item xs={12} sm={9}>
            <form ref={formRef}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <LFCSelectForm
                    name="use_type"
                    label={t('ユーザ種別')}
                    value={values.use_type}
                    onChange={event => handleInputChange2(event, values, setValues)}
                    required
                    fullWidth
                    selectItem={useTypeItems}
                    id={'use_type'}
                  />
                </Grid>
                <Grid item xs={12}>
                  <LFCSelectFormCompany
                    name={'company_id'}
                    value={values.company_id}
                    onChange={event => handleInputChange2(event, values, setValues)}
                    required
                    fullWidth
                  />
                </Grid>{' '}
                <Grid item xs={6}>
                  <LFCTextField
                    name="user_id"
                    label={t('ユーザID')}
                    value={values.user_id}
                    onChange={event => {
                      handleInputChange(event, values, setValues);
                    }}
                    other={{
                      required: true,
                      inputProps: {minLength: 3, maxLength: 20, pattern: '^[a-z0-9_.\\-]*$'}
                    }}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6}>
                  <LFCTextField
                    name="user_name"
                    label={t('ユーザー名')}
                    value={values.user_name}
                    onChange={event => {
                      handleInputChange(event, values, setValues);
                    }}
                    required
                    fullWidth
                    inputProps={{maxLength: 30}}
                  />
                </Grid>
                <Grid item xs={12}>
                  <LFCTextField
                    name="email"
                    label={t('メールアドレス')}
                    value={values.email}
                    onChange={event => {
                      handleInputChange(event, values, setValues);
                    }}
                    other={{
                      required: true,
                      inputProps: {
                        pattern: '^[a-z0-9_.+\\-]+@([a-z0-9][a-z0-9\\-]*\\.)+[a-z]{2,}$'
                      }
                    }}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6}>
                  <LFCSelectForm
                    id={'locale'}
                    name="locale"
                    label={t('表示言語')}
                    value={values.locale}
                    onChange={event => handleInputChange2(event, values, setValues)}
                    required
                    fullWidth
                    selectItem={localeTypes}
                  />
                </Grid>
                <Grid item xs={6}>
                  <LFCDatePicker
                    name={'effective_date'}
                    label={t('有効期限')}
                    value={values.effective_date}
                    onChange={event => {
                      handleInputChange(event, values, setValues);
                    }}
                    required
                    fullWidth
                  />
                </Grid>
              </Grid>
            </form>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <LFCButton onClick={() => props.onClose(false)}>{t('キャンセル')}</LFCButton>
        <LFCButton color="primary" onClick={onCreateUser}>
          {t('保存')}
        </LFCButton>
      </DialogActions>
    </Dialog>
  );
};
export default UserCreateDialog;
